import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BellRing, X } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import usePushNotificaciones from '../hooks/usePushNotificaciones';

const DIAS_PARA_VOLVER_A_PREGUNTAR = 7;

/**
 * Aviso flotante en la esquina inferior (no tapa la app) para activar los avisos del navegador. Si la persona responde
 * "No volver a preguntar" se guarda en el servidor y no se le vuelve a mostrar en ningún equipo;
 * "Ahora no" pospone la invitación una semana. Siempre puede activarlos después desde la campana.
 */
export default function AvisosNavegadorPrompt() {
    const { user } = useAuth();
    const push = usePushNotificaciones();
    const [visible, setVisible] = useState(false);
    const [ayudaBloqueo, setAyudaBloqueo] = useState(false);
    const decidido = useRef(false);
    const clave = user?.id ? `avisosNavegador_${user.id}` : null;

    useEffect(() => {
        if (!clave || decidido.current || push.estado === 'no-soportado' || push.estado === 'verificando') return undefined;
        let cancelado = false;
        (async () => {
            let rechazado = false;
            try { rechazado = (await api.get('/push/preferencia')).data.rechazado; } catch { /* sin servidor: no molestar */ return; }
            if (cancelado || rechazado) return;
            decidido.current = true;

            // Permiso ya concedido pero este navegador aún no está registrado: se registra sin preguntar
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                if (push.estado === 'desactivado') push.activar();
                return;
            }
            if (push.estado !== 'desactivado') return; // bloqueado u ya activado
            try {
                const hasta = Number(localStorage.getItem(clave) || 0);
                if (hasta > Date.now()) return;
            } catch { /* sin localStorage */ }
            setTimeout(() => { if (!cancelado) setVisible(true); }, 4000);
        })();
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clave, push.estado]);

    if (!visible) return null;

    const posponer = () => {
        try { localStorage.setItem(clave, String(Date.now() + DIAS_PARA_VOLVER_A_PREGUNTAR * 86400000)); } catch { /* ignora */ }
        setVisible(false);
    };
    const noVolver = async () => {
        try { await api.put('/push/preferencia', { rechazado: true }); } catch { posponer(); return; }
        setVisible(false);
    };
    const activar = async () => {
        await push.activar();
        // si Chrome mostró su aviso "silenciado" o la persona lo negó, se explica cómo habilitarlo
        if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') setAyudaBloqueo(true);
        else setVisible(false);
    };

    return createPortal(
        <div role="dialog" aria-label="Activar avisos del navegador"
            className="fixed z-[10000] bottom-4 right-4 left-4 sm:left-auto sm:w-[23rem] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl p-4"
            style={{ animation: 'avisosNavSube .25s ease-out' }}>
            <style>{'@keyframes avisosNavSube{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}'}</style>
            <div className="flex items-start gap-3">
                <span className="mt-0.5 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 shrink-0"><BellRing size={18} /></span>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Activa los avisos del navegador</p>
                    <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">
                        Te avisamos cuando te asignen un ticket o te respondan, aunque no tengas el portal abierto.
                    </p>
                </div>
                <button onClick={posponer} aria-label="Cerrar" className="p-0.5 text-gray-400 hover:text-gray-600 shrink-0"><X size={16} /></button>
            </div>
            {ayudaBloqueo && (
                <div className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-2.5 text-[11px] leading-snug text-amber-800 dark:text-amber-200">
                    Si Chrome mostró "Notificaciones bloqueadas", presiona <b>Permitir para este sitio</b>. Si ya no aparece, haz clic en el candado junto a la dirección,
                    abre <b>Configuración del sitio</b>, cambia <b>Notificaciones</b> a <b>Permitir</b> y recarga la página.
                </div>
            )}
            <div className="mt-3 flex items-center justify-between gap-2">
                <button onClick={noVolver} className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline-offset-2 hover:underline">No volver a preguntar</button>
                <div className="flex items-center gap-2">
                    <button onClick={posponer} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Ahora no</button>
                    <button onClick={activar} disabled={push.trabajando} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                        {push.trabajando ? 'Activando…' : 'Activar'}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
